import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { namenKern, readBlattNamen } from "@/office/excel/analyse";
import { nichtPruefbar } from "../schnittstelle";

/** Namen, die Excel beim Spezialfilter selbst anlegt – deutsch und englisch. */
const KRITERIEN = ["kriterien", "criteria"];
const ZIEL = ["extrahieren", "extract"];

export const lektionSpezialfilter: Lesson = {
  id: "spezialfilter",
  title: "Der Spezialfilter",
  summary: "Filtern mit ODER über mehrere Spalten – und das Ergebnis an eine andere Stelle kopieren.",
  durationMinutes: 10,
  learningGoals: [
    "Einen Kriterienbereich anlegen",
    "Mit dem Spezialfilter UND- und ODER-Bedingungen kombinieren",
    "Das Ergebnis an eine andere Stelle kopieren",
  ],
  steps: [
    {
      id: "kriterienbereich",
      title: "Der Kriterienbereich",
      explanation: [
        "Der AutoFilter verknüpft Bedingungen verschiedener Spalten immer mit UND. „Region " +
          "Nord oder Betrag über 5000“ kann er nicht. Dafür gibt es den Spezialfilter.",
        "Er braucht einen Kriterienbereich – ein paar Zellen, meist oberhalb der Liste: " +
          "in der ersten Zeile dieselben Spaltenüberschriften wie in der Liste, darunter die " +
          "Bedingungen. Bedingungen in derselben Zeile gelten zusammen (UND), Bedingungen in " +
          "verschiedenen Zeilen alternativ (ODER).",
        "„Daten“ → „Erweitert“ öffnet den Dialog: Listenbereich, Kriterienbereich und " +
          "wahlweise „An eine andere Stelle kopieren“. Excel merkt sich die Bereiche unter " +
          "den Namen „Kriterien“ und „Extrahieren“.",
      ],
      keyPoints: [
        "Kriterienbereich: Überschriften wie in der Liste, darunter die Bedingungen.",
        "Gleiche Zeile = UND, verschiedene Zeilen = ODER.",
        "Daten → Erweitert, auf Wunsch an eine andere Stelle kopieren.",
      ],
      task: {
        instruction:
          "Lege über einer Liste einen Kriterienbereich mit einer ODER-Bedingung an und " +
          "filtere die Liste mit „Daten“ → „Erweitert“ – das Ergebnis an eine andere Stelle kopiert.",
        hint: "Kriterien z. B. in F1:G3: F1 „Region“, G1 „Betrag“, F2 „Nord“, G3 „>5000“. Dann Daten → Erweitert.",
        check: async () => {
          const namen = await readBlattNamen();
          if (namen === null) {
            return nichtPruefbar(
              "Die blattbezogenen Namen",
              "1.4",
              "Im Namens-Manager (Strg + F3) müssen „Kriterien“ und „Extrahieren“ stehen.",
              true,
            );
          }
          const kern = namen.map((name) => namenKern(name.name));
          return fromCriteria(
            [
              criterion("Ein Kriterienbereich wurde verwendet", kern.some((name) => KRITERIEN.includes(name))),
              criterion("Das Ergebnis wurde an eine andere Stelle kopiert", kern.some((name) => ZIEL.includes(name))),
            ],
            {
              success: "Gefiltert mit ODER über zwei Spalten – das kann nur der Spezialfilter.",
              failure: "Der Spezialfilter wurde noch nicht vollständig angewendet.",
              hint: "Im Dialog „An eine andere Stelle kopieren“ wählen und eine freie Zielzelle angeben.",
            },
          );
        },
      },
    },
  ],
};
